// components/AllBooks.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

function AllBooks() {
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  const BOOKS_PER_PAGE = 24;
  const observer = useRef();
  
  // Function to fetch all books (we'll implement paging client-side)
  const fetchAllBooks = async () => {
    try {
      setLoading(true);
      // Making a request without authorId or categoryId to get all books
      const response = await api.getBooks(null, null);
      setBooks(response);
      setInitialLoadComplete(true);
      setLoading(false);
    } catch (err) {
      setError('Failed to load books');
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchAllBooks();
  }, []);

  // Filter and paginate books when search term changes or on initial load
  useEffect(() => {
    if (!initialLoadComplete) return;
    
    const filtered = books.filter(book => 
      book.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredBooks(filtered.slice(0, page * BOOKS_PER_PAGE));
    setHasMore(filtered.length > page * BOOKS_PER_PAGE);
  }, [searchTerm, books, page, initialLoadComplete]);

  // Last book element reference for intersection observer (infinite scroll)
  const lastBookElementRef = useCallback(node => {
    if (loading) return;
    
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page when search term changes
  };

  if (error) return <div className="error">{error}</div>;

  return (
    <div className="all-books-page">
      <h2>ყველა წიგნი</h2>
      
      <div className="search-container">
        <input
          type="text"
          placeholder="მოძებნე წიგნი..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="search-input"
        />
      </div>
      
      {!initialLoadComplete ? (
        <div className="loading">წიგნები იტვირთება...</div>
      ) : (
        <>
          <div className="book-count">
        {filteredBooks.length} წიგნი {searchTerm ? 
              books.filter(book => book.name.toLowerCase().includes(searchTerm.toLowerCase())).length : 
              books.length} - დან 
          </div>
          
          <div className="all-books-grid">
            {filteredBooks.map((book, index) => {
              // If this is the last element and we have more items, attach the ref
              const isLastElement = index === filteredBooks.length - 1;
              
              return (
                <Link 
                  to={`/book/${book.id}`}
                  key={book.id}
                  className="book-card"
                  ref={isLastElement && hasMore ? lastBookElementRef : null}
                >
                  <div className="book-image">
                    <img 
                      src={`/images/books/${book.id}.jpg`} 
                      alt={book.name} 
onError={(e) => {e.target.src = '/images/default-book.jpg'}}
                    />
                  </div>
                  <div className="book-details">
                    <h4>{book.name}</h4>
                    <p className="book-author">By {book.authorName}</p>
                  </div>
                </Link>
              );
            })}
          </div>
          
{loading && <div className="loading-more">იტვირთება უფრო მეტი...</div>}
          
          {!hasMore && books.length > 0 && (
            <div className="end-message">სიის ბოლოში ხარ</div>
          )}
        </>
      )}
    </div>
  );
}

export default AllBooks;